import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { MasterTransaction } from '../types';
import { isAllocated, isPos, isAllocatedOrPendingPOS } from '../lib/transactionUtils';
import { parseTransactionDate } from '../lib/utils';
import { getStaff } from './staffService';

export interface EmployeeEarnings {
  staffId: string;
  staffName: string;
  posId?: string;
  payrollId?: string;
  email?: string;
  role?: string;
  totalEarnings: number;
  posEarnings: number;
  cashEarnings: number;
}

export const getEmployeeEarningsOverview = async (
  tenantId: string,
  startDate?: string,
  endDate?: string,
  sourceFilter?: string
): Promise<EmployeeEarnings[]> => {
  const txRef = collection(db, 'tenants', tenantId, 'master_transactions');

  // Fetch all transactions and staff list to support parent source mapping for split pool children accurately
  const [snapshot, staffList] = await Promise.all([
    getDocs(txRef),
    getStaff(tenantId)
  ]);
  
  const allDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MasterTransaction));

  // Helper to find canonical staff profile for a transaction
  const findCanonicalStaff = (tx: MasterTransaction) => {
    if (!staffList || staffList.length === 0) return null;
    
    // 1. Try matching by email
    if (tx.email && tx.email !== 'N/A' && tx.email !== 'unknown') {
      const match = staffList.find(s => s.email?.toLowerCase().trim() === tx.email?.toLowerCase().trim());
      if (match) return match;
    }
    
    // 2. Try matching by staffId
    if (tx.staffId && tx.staffId !== 'unknown') {
      const match = staffList.find(s => s.id === tx.staffId);
      if (match) return match;
    }
    
    // 3. Try matching by staffName
    if (tx.staffName) {
      const txNameNormalized = tx.staffName.toLowerCase().trim();
      const match = staffList.find(s => s.name?.toLowerCase().trim() === txNameNormalized);
      if (match) return match;
    }
    
    return null;
  };

  // Filter for only Allocated transactions in the range first for earnings computation
  let filtered = allDocs.filter(t => isAllocatedOrPendingPOS(t, allDocs));
  
  if (startDate || endDate) {
    filtered = filtered.filter(t => {
      const transactionDate = parseTransactionDate(t.tipDate || t.timestamp);
      if (startDate && transactionDate < startDate) return false;
      if (endDate && transactionDate > endDate) return false;
      return true;
    });
  }

  // Filter by source if requested (this filters transactions, not aggregated earnings)
  if (sourceFilter === 'POS') {
      filtered = filtered.filter(t => isPos(t, allDocs));
  } else if (sourceFilter === 'Cash') {
      filtered = filtered.filter(t => !isPos(t, allDocs));
  }
  
  // Aggregate
  const earningsMap: Record<string, EmployeeEarnings> = {};

  filtered.forEach(t => {
    const canonicalStaff = findCanonicalStaff(t);
    const staffId = canonicalStaff?.id || t.staffId || t.staffName || 'unknown';
    const staffName = canonicalStaff?.name || t.staffName || 'Unknown';

    const posId = canonicalStaff?.posId || t.posId;
    const payrollId = canonicalStaff?.payrollId;
    const email = canonicalStaff?.email || t.email;
    const role = canonicalStaff?.role;

    if (!earningsMap[staffId]) {
      earningsMap[staffId] = { 
        staffId: staffId, 
        staffName: staffName,
        posId: posId,
        payrollId: payrollId,
        email: email,
        role: role,
        totalEarnings: 0,
        posEarnings: 0,
        cashEarnings: 0
      };
    } else {
      if (!earningsMap[staffId].posId && posId) earningsMap[staffId].posId = posId;
      if (!earningsMap[staffId].payrollId && payrollId) earningsMap[staffId].payrollId = payrollId;
      if (!earningsMap[staffId].email && email) earningsMap[staffId].email = email;
      if (!earningsMap[staffId].role && role) earningsMap[staffId].role = role;
    }
    const amount = t.amount || 0;
    earningsMap[staffId].totalEarnings += amount;
    
    // Check if it's POS or Cash using true parent transactions classifications
    if (isPos(t, allDocs)) {
      earningsMap[staffId].posEarnings += amount;
    } else {
      earningsMap[staffId].cashEarnings += amount;
    }
  });

  return Object.values(earningsMap).sort((a, b) => b.totalEarnings - a.totalEarnings);
};
